import type { AuthUser, Profile } from "@pra/shared/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type * as React from "react";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, endpoints, getScopeUserId, setScopeUserId } from "./api";

export type Session = {
  user: AuthUser | undefined;
  profile: Profile | undefined;
  loading: boolean;
  isAdmin: boolean;
  /** Admin view-as: the user whose data the pages show. */
  scopeUserId: string | undefined;
  setScope: (userId: string | undefined) => void;
  refresh: () => Promise<void>;
};

const SessionContext = createContext<Session | undefined>(undefined);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await endpoints.me();
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
  const [scope, setScopeState] = useState<string | undefined>(() => {
    try {
      return sessionStorage.getItem("pra.scope") ?? undefined;
    } catch {
      return undefined;
    }
  });
  const user = query.data?.user;
  const isAdmin = user?.role === "admin";
  const effectiveScope = isAdmin ? scope : undefined;
  // Only (re)apply the scope once we know whether the user is an admin, and refetch everything when
  // it actually changes — otherwise the first render would clear a stored scope before /me resolves.
  const applied = useRef<string | undefined>(getScopeUserId());
  useEffect(() => {
    if (query.isLoading) return;
    if (applied.current === effectiveScope) return;
    setScopeUserId(effectiveScope);
    applied.current = effectiveScope;
    void client.invalidateQueries();
  }, [effectiveScope, query.isLoading, client]);
  const value = useMemo<Session>(
    () => ({
      user,
      profile: query.data?.profile,
      loading: query.isLoading,
      isAdmin,
      scopeUserId: effectiveScope,
      setScope: (userId) => {
        setScopeState(userId);
        setScopeUserId(isAdmin ? userId : undefined);
        applied.current = isAdmin ? userId : undefined;
        try {
          if (userId === undefined) sessionStorage.removeItem("pra.scope");
          else sessionStorage.setItem("pra.scope", userId);
        } catch {
          /* ignore */
        }
        void client.invalidateQueries();
      },
      refresh: async () => {
        await client.invalidateQueries({ queryKey: ["me"] });
      },
    }),
    [user, query.data?.profile, query.isLoading, isAdmin, effectiveScope, client],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const ctx = useContext(SessionContext);
  if (ctx === undefined) throw new Error("useSession outside SessionProvider");
  return ctx;
}
